// Staff chat display helpers: times, time lines, roles and thread names. Pure, so the dock and the home card agree.
import type { StaffChatPerson, StaffChatThread } from '@/staff-chat/types/staff-chat';

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

function isYesterday(d: Date, now: Date): boolean {
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  return dayKey(d) === dayKey(y);
}

/** A message's time in a bubble: "10:42". */
export function bubbleTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** A thread row's time: today "10:42", "Yesterday", then "3 Nov". */
export function listTime(iso: string | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (dayKey(d) === dayKey(now)) return bubbleTime(iso);
  if (isYesterday(d, now)) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

/** A thread's centred time line: "Today 09:15", "Yesterday 14:02", then "3 Nov 09:15". */
export function timeLine(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = bubbleTime(iso);
  if (dayKey(d) === dayKey(now)) return `Today ${time}`;
  if (isYesterday(d, now)) return `Yesterday ${time}`;
  return `${d.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${time}`;
}

export const HOUR_MS = 60 * 60 * 1000;

/** A time line goes before a message when it is the first one, or comes an hour or more after the one before it. */
export const startsTimeLine = (prev: string | undefined, next: string): boolean =>
  !prev || Date.parse(next) - Date.parse(prev) >= HOUR_MS || differentDay(prev, next);

const ROLE_LABEL: Record<StaffChatPerson['kind'], string> = { practitioner: 'Practitioner', staff: 'Staff' };

/** A person's role line ("Practitioner", "Staff"): the directory's `kind`, the only role the contract carries. */
export const roleLabel = (p: Pick<StaffChatPerson, 'kind'> | undefined): string => (p ? (ROLE_LABEL[p.kind] ?? '') : '');

/** True when two instants fall on different local days (a divider goes between them). */
export const differentDay = (a: string, b: string): boolean => dayKey(new Date(a)) !== dayKey(new Date(b));

/** The colleagues in a thread: everyone but the caller. */
export const otherParticipants = (t: Pick<StaffChatThread, 'participants'>, me: string | undefined): StaffChatPerson[] =>
  t.participants.filter((p) => p.userId !== me);

/** The role line of a one-to-one thread: the other person's role. */
export const threadRole = (t: Pick<StaffChatThread, 'participants'>, me: string | undefined): string => roleLabel(otherParticipants(t, me)[0]);

/** Unread conversations first, then the server's order (newest first). The sort is stable. */
export const unreadFirst = <T extends Pick<StaffChatThread, 'unreadCount'>>(threads: readonly T[]): T[] =>
  [...threads].sort((a, b) => Number(b.unreadCount > 0) - Number(a.unreadCount > 0));

/** A thread's name in the list and header: the other participants' names. */
export function threadTitle(t: Pick<StaffChatThread, 'participants'>, me: string | undefined): string {
  const names = otherParticipants(t, me).map((p) => p.name).filter(Boolean);
  return names.length > 0 ? names.join(', ') : 'Colleague';
}

/** The patient line of a patient thread, or null. A patient the server could not read is said so, never guessed. */
export function threadPatientLine(t: Pick<StaffChatThread, 'kind' | 'patient'>): string | null {
  if (t.kind !== 'patient' || !t.patient) return null;
  return t.patient.name ? `Patient: ${t.patient.name}` : 'Patient record unavailable';
}

/** "You: " before the caller's own last message. */
export function previewLine(t: Pick<StaffChatThread, 'lastMessage'>, me: string | undefined): string {
  const m = t.lastMessage;
  if (!m) return 'No messages yet';
  if (!m.preview) return 'Message deleted';
  return m.senderUserId === me ? `You: ${m.preview}` : m.preview;
}

export const unreadLabel = (n: number): string => `${n} unread ${n === 1 ? 'message' : 'messages'}`;

/** A fresh id per message sent; the contract allows 1..64 characters. Kept on a retry. */
export const newClientMessageId = (): string => crypto.randomUUID();
